export type RouterErrorType = Error | string;

export type RouterErrorLogger = (error: RouterErrorType) => void;

export type RouterLogType = 'log' | 'info' | 'warn' | 'error';

let __routerLoggingActive: boolean = true;

let __routeErrorReporter: RouterErrorLogger;
let __routeErrorLogger: RouterErrorLogger;

/**
 *	Enables router logging. Does not affect error reporting.
 */
export function enableRouterLogging(): void {
	__routerLoggingActive = true;
}

/**
 *	Disables router logging. Does not affect error reporting.
 */
export function disableRouterLogging(): void {
	__routerLoggingActive = false;
}

/**
 *	Registers a new error logger.
 *
 *	@param RouterErrorLogger errorReporter
 *
 *	@return void
 */
export function registerErrorLogger(errorLogger: RouterErrorLogger): void {
	__routeErrorLogger = errorLogger;
}

/**
 *	Registers a new error reporter.
 *
 *	@param RouterErrorLogger errorReporter
 *
 *	@return void
 */
export function registerErrorReorter(errorReporter: RouterErrorLogger): void {
	__routeErrorReporter = errorReporter;
}

/**
 *	Returns registered, or default error logger if not set.
 *
 * @returns RouterErrorLogger
 */
export function getRouterErrorLogger(): RouterErrorLogger {
	const defaultErrorLogger: RouterErrorLogger = (error: RouterErrorType) =>
		console.error(error);

	return __routeErrorLogger || defaultErrorLogger;
}

/**
 *	Logs something to console if router logging is enabled.
 *
 *	@param T entity
 *	@param RouterLogType type
 */
export function logRouterEntity<T = any>(
	entity: T,
	type: RouterLogType = 'info'
): void {
	if (type in console && __routerLoggingActive) {
		console[type](entity);
	}
}

/**
 *	Logs an error with registered error logger, defaults to `console.error`.
 *
 *	@param RouterErrorType error
 *
 *	@return void
 */
export function logRouterError(error: RouterErrorType): void {
	if (__routerLoggingActive) {
		getRouterErrorLogger()(error);
	}
}

/**
 *	Reports router errors, might be useful when using something like Sentry.
 *	This will always report, even if logging is disabled, but only if a reporter has been registered with `registerErrorReorter`.
 *
 *	@param RouterErrorType error
 *
 *	@return void
 */
export function reportRouterError(error: RouterErrorType): void {
	if (typeof __routeErrorReporter === 'function') {
		__routeErrorReporter(error);
	}
}
